import React, { useEffect, useMemo, useRef } from 'react'
import { useUpdate } from 'react-use'
import { DateTime, Duration } from 'luxon'

import { useLiveDataQuery } from '../../../global/blocs/useLiveData'
import { useInterval } from '../../../sharedLegacy/hooks'

interface CountdownComponentProps {
  timestamp: number | string
  onCountdownZero?: () => void
}

// Largest-to-smallest units used to humanize the remaining/elapsed time without
// a prefix/suffix — matching the old moment `fromNow(true)` behavior.
const HUMANIZE_UNITS = [
  'years',
  'months',
  'days',
  'hours',
  'minutes',
  'seconds',
] as const

const UNIT_MAP = {
  years: 'year',
  months: 'month',
  days: 'day',
  hours: 'hour',
  minutes: 'minute',
  seconds: 'second',
} as const satisfies Record<(typeof HUMANIZE_UNITS)[number], string>

/** Localized "2 days" / "3 hours" (no "in"/"ago"), like moment `fromNow(true)`. */
const humanizeWithoutSuffix = (deadline: DateTime): string => {
  const diff = deadline.diffNow().shiftTo(...HUMANIZE_UNITS)
  const unit =
    HUMANIZE_UNITS.find((u) => Math.abs(diff.get(u)) >= 1) ?? 'seconds'
  const value = Math.floor(Math.abs(diff.get(unit)))

  return new Intl.NumberFormat(deadline.locale ?? undefined, {
    style: 'unit',
    unit: UNIT_MAP[unit],
    unitDisplay: 'long',
  }).format(value)
}

const CountdownComponent = ({
  timestamp,
  onCountdownZero,
}: CountdownComponentProps) => {
  const { data: liveData } = useLiveDataQuery()

  const deadline = useMemo(() => {
    const d = DateTime.fromSeconds(Number(timestamp))
    // `liveData.locale` is a POSIX-style locale (e.g. `en_US`); luxon/Intl need
    // BCP 47 (`en-US`).
    const locale = liveData?.locale.replace(/_/g, '-')
    return locale ? d.setLocale(locale) : d
  }, [timestamp, liveData?.locale])

  const update = useUpdate()
  useInterval(update, 1000)

  const duration = Math.trunc(deadline.diffNow('seconds').seconds)

  const hasFiredRef = useRef(false)

  useEffect(() => {
    if (duration < 0 && !hasFiredRef.current) {
      onCountdownZero?.()
      hasFiredRef.current = true
    } else if (duration >= 0 && hasFiredRef.current) {
      hasFiredRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration])

  if (duration >= 86400) {
    return (
      <div className="countdown-component">
        {humanizeWithoutSuffix(deadline)}
      </div>
    )
  }

  if (duration >= 0) {
    return (
      <div className="countdown-component">
        <span>{Duration.fromMillis(duration * 1000).toFormat('hh:mm:ss')}</span>
      </div>
    )
  }

  return (
    <div className="countdown-component">{humanizeWithoutSuffix(deadline)}</div>
  )
}

export { CountdownComponent }
